import { days, getDayDisplayOrderIndex, getDayLabel } from "@ac-expo/api-types";
import { Day, EventTimetableState } from "@prisma/client";
import {
  addDays,
  differenceInCalendarDays,
  format,
  isAfter,
  isSameMonth,
  isSameYear,
} from "date-fns";
import { fr } from "date-fns/locale";
import { DateRange } from "./date-ranges.utils";
import {
  EventExceptionalClosingDateType,
  EventScheduleType,
  formatTimetableHours,
  fromDatabaseFormat,
  getGroupedDatesString,
  getGroupedDatesStringExtremities,
  toDatabaseFormat,
} from "./dates";

type ScheduleTimetable = EventScheduleType["timetables"][number];

/**
 * The dates of an event that share the very same week timetables, as shown to a visitor.
 * Purely a display grouping: several schedules collapse into one rhythm.
 */
export type OpeningRhythm = {
  timetables: ScheduleTimetable[];
  openedDays: Day[];
  dates: string[];
  ranges: DateRange[];
  /** First to last opening date, whatever happens in between. */
  span: DateRange;
  /**
   * True when the organiser set this rhythm up as a period running longer than a week, rather
   * than as a handful of separate dates. Such a rhythm reads as a span plus its week days;
   * listing each date of it would only repeat what the week days already say.
   */
  runsOverWeeks: boolean;
};

export type OpeningRhythmTimetableLine = {
  days: string | null;
  hours: string;
};

const byDisplayOrder = (a: ScheduleTimetable, b: ScheduleTimetable) =>
  getDayDisplayOrderIndex(a.day) - getDayDisplayOrderIndex(b.day);

const getTimetableSignature = (timetable: ScheduleTimetable) =>
  [
    timetable.day,
    timetable.state,
    timetable.openingTime,
    timetable.closingTime,
    timetable.breakStartTime,
    timetable.breakEndTime,
  ].join("|");

const getOpenedTimetables = (timetables: ScheduleTimetable[]) =>
  [...timetables]
    .filter((timetable) => timetable.state !== EventTimetableState.closed)
    .sort(byDisplayOrder);

const getRhythmKey = (openedTimetables: ScheduleTimetable[]) =>
  openedTimetables.map(getTimetableSignature).join("//");

// A period of more than a week is a rhythm the visitor reads by its week days, not date by date.
const isLongPeriod = (schedule: EventScheduleType) =>
  differenceInCalendarDays(
    fromDatabaseFormat(schedule.toDate),
    fromDatabaseFormat(schedule.fromDate)
  ) > 7;

const getScheduleOpeningDates = (
  schedule: EventScheduleType,
  openedTimetables: ScheduleTimetable[]
) => {
  const openedDays = new Set(
    openedTimetables.map((timetable) => timetable.day)
  );

  const endDate = fromDatabaseFormat(schedule.toDate);
  const openingDates: string[] = [];

  let currentDate = fromDatabaseFormat(schedule.fromDate);

  while (!isAfter(currentDate, endDate)) {
    if (openedDays.has(days[currentDate.getDay()])) {
      openingDates.push(toDatabaseFormat(currentDate));
    }

    currentDate = addDays(currentDate, 1);
  }

  return openingDates;
};

/**
 * Collapses the schedules of an event into opening rhythms: one entry per distinct set of
 * week timetables, carrying every date it opens on, closing dates excluded.
 */
export const groupSchedulesByTimetables = (
  schedules: EventScheduleType[],
  exceptionalClosingDates: EventExceptionalClosingDateType[]
): OpeningRhythm[] => {
  const closedDates = new Set(exceptionalClosingDates.map(({ date }) => date));

  const datesByRhythm = new Map<
    string,
    {
      timetables: ScheduleTimetable[];
      dates: Set<string>;
      runsOverWeeks: boolean;
    }
  >();

  schedules.forEach((schedule) => {
    const openedTimetables = getOpenedTimetables(schedule.timetables);

    if (openedTimetables.length === 0) return;

    const key = getRhythmKey(openedTimetables);

    const rhythm = datesByRhythm.get(key) ?? {
      timetables: openedTimetables,
      dates: new Set<string>(),
      runsOverWeeks: false,
    };

    getScheduleOpeningDates(schedule, openedTimetables)
      .filter((date) => !closedDates.has(date))
      .forEach((date) => rhythm.dates.add(date));

    datesByRhythm.set(key, {
      ...rhythm,
      runsOverWeeks: rhythm.runsOverWeeks || isLongPeriod(schedule),
    });
  });

  return [...datesByRhythm.values()]
    .flatMap<OpeningRhythm>(({ timetables, dates, runsOverWeeks }) => {
      if (dates.size === 0) return [];

      const sortedDates = [...dates].sort();

      const openedDays = [
        ...new Set(
          sortedDates.map((date) => days[fromDatabaseFormat(date).getDay()])
        ),
      ].sort((a, b) => getDayDisplayOrderIndex(a) - getDayDisplayOrderIndex(b));

      return [
        {
          timetables,
          openedDays,
          dates: sortedDates,
          ranges: getGroupedDatesStringExtremities(
            getGroupedDatesString([...sortedDates])
          ),
          span: {
            from: sortedDates[0],
            to: sortedDates[sortedDates.length - 1],
          },
          runsOverWeeks,
        },
      ];
    })
    .sort((a, b) => a.dates[0].localeCompare(b.dates[0]));
};

const formatDayRun = (run: Day[]) =>
  run.length > 2
    ? `${getDayLabel(run[0])} - ${getDayLabel(run[run.length - 1])}`
    : run.map(getDayLabel).join(", ");

const formatDayLabels = (dayList: Day[]) => {
  const runs: Day[][] = [];

  dayList.forEach((day) => {
    const currentRun = runs[runs.length - 1];

    const isConsecutive =
      currentRun &&
      getDayDisplayOrderIndex(day) -
        getDayDisplayOrderIndex(currentRun[currentRun.length - 1]) ===
        1;

    if (isConsecutive) currentRun.push(day);
    else runs.push([day]);
  });

  return runs.map(formatDayRun).join(", ");
};

/**
 * The hours of a rhythm, as one line per set of week days sharing the same hours.
 * A rhythm with a single set of hours needs no day label: its dates already say when it opens.
 */
export const formatOpeningRhythmTimetables = (
  rhythm: OpeningRhythm
): OpeningRhythmTimetableLine[] => {
  const daysByHours = new Map<string, Day[]>();

  rhythm.openedDays.forEach((day) => {
    const timetable = rhythm.timetables.find(
      (candidate) => candidate.day === day
    );

    if (!timetable) return;

    const hours = formatTimetableHours(timetable);
    daysByHours.set(hours, [...(daysByHours.get(hours) ?? []), day]);
  });

  const lines = [...daysByHours.entries()];

  // A single set of hours needs a day label only when the rhythm is read as a span: there the
  // dates are not listed, so the week days are the only thing saying when it actually opens.
  const needsDayLabels =
    lines.length > 1 ||
    (rhythm.runsOverWeeks && rhythm.openedDays.length < days.length);

  if (!needsDayLabels) return [{ days: null, hours: lines[0][0] }];

  return lines.map(([hours, dayList]) => ({
    days: formatDayLabels(dayList),
    hours,
  }));
};

export type OpeningStatus =
  | { kind: "openToday"; date: string; hours: string }
  | { kind: "upcoming"; date: string; hours: string };

const findTodayTimetable = (rhythm: OpeningRhythm, date: string) =>
  rhythm.timetables.find(
    (timetable) => timetable.day === days[fromDatabaseFormat(date).getDay()]
  );

/**
 * What a visitor wants to know first: whether the event opens today, and otherwise when it
 * opens next. Returns nothing once every opening date has passed.
 */
export const findOpeningStatus = (
  rhythms: OpeningRhythm[],
  today: Date
): OpeningStatus | null => {
  const todayDate = toDatabaseFormat(today);

  const openingDates = rhythms
    .flatMap((rhythm) => rhythm.dates.map((date) => ({ rhythm, date })))
    .filter(({ date }) => date >= todayDate)
    .sort((a, b) => a.date.localeCompare(b.date));

  const nextOpening = openingDates[0];

  if (!nextOpening) return null;

  const timetable = findTodayTimetable(nextOpening.rhythm, nextOpening.date);

  if (!timetable) return null;

  return {
    kind: nextOpening.date === todayDate ? "openToday" : "upcoming",
    date: nextOpening.date,
    hours: formatTimetableHours(timetable),
  };
};

// French names the first of a month as an ordinal, and every other day as a plain number.
const formatDayNumber = (date: Date) =>
  date.getDate() === 1 ? "1er" : format(date, "d");

const formatMonthYear = (date: Date) =>
  format(date, "MMMM yyyy", { locale: fr });

const joinWithAnd = (parts: string[]) =>
  parts.length < 2
    ? parts.join("")
    : `${parts.slice(0, -1).join(", ")} et ${parts[parts.length - 1]}`;

const isWithinOneMonth = (range: DateRange) => {
  const from = fromDatabaseFormat(range.from);
  const to = fromDatabaseFormat(range.to);

  return isSameMonth(from, to) && isSameYear(from, to);
};

const formatCrossMonthRange = (range: DateRange) => {
  const from = fromDatabaseFormat(range.from);
  const to = fromDatabaseFormat(range.to);

  const fromLabel = isSameYear(from, to)
    ? `${formatDayNumber(from)} ${format(from, "MMMM", { locale: fr })}`
    : `${formatDayNumber(from)} ${formatMonthYear(from)}`;

  return `${fromLabel} au ${formatDayNumber(to)} ${formatMonthYear(to)}`;
};

/** One written line of dates, with the last date it covers so a reader can tell it has passed. */
export type OpeningDateLine = {
  label: string;
  lastDate: string;
};

/**
 * The dates of a rhythm, written the way one would say them out loud: one line per month,
 * the month named once at the end. A range spanning two months keeps a line of its own.
 */
export const formatOpeningDateLines = (
  ranges: DateRange[]
): OpeningDateLine[] => {
  const lines: OpeningDateLine[] = [];
  const monthParts = new Map<string, { parts: string[]; lastDate: string }>();

  const flushMonth = (monthYear: string) => {
    const month = monthParts.get(monthYear);

    if (!month) return;

    lines.push({
      label: `${joinWithAnd(month.parts)} ${monthYear}`,
      lastDate: month.lastDate,
    });

    monthParts.delete(monthYear);
  };

  ranges.forEach((range) => {
    if (!isWithinOneMonth(range)) {
      [...monthParts.keys()].forEach(flushMonth);
      lines.push({ label: formatCrossMonthRange(range), lastDate: range.to });

      return;
    }

    const from = fromDatabaseFormat(range.from);
    const monthYear = formatMonthYear(from);

    const part =
      range.from === range.to
        ? formatDayNumber(from)
        : `${formatDayNumber(from)} au ${formatDayNumber(fromDatabaseFormat(range.to))}`;

    monthParts.set(monthYear, {
      parts: [...(monthParts.get(monthYear)?.parts ?? []), part],
      lastDate: range.to,
    });
  });

  [...monthParts.keys()].forEach(flushMonth);

  return lines;
};
